import { useEffect, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { navigate, href } from '../../app/router';
import type { Board } from '../../lib/api';
import { isEditing } from '../../ui/escape';
import { useBulk } from './BulkBar';
import type { ItemMenu } from './ItemMenu';
import type { BoardActionId, Denied } from './model/actions';
import { selectAll, type Selection } from './model/selection';
import { useNotify } from './undo';

/** Dashboard shortcuts (P16-4), listed by the settings page; arrows stand for all four. */
export const DASHBOARD_SHORTCUTS = [
  { combo: 'Enter', key: 'dashboard.keys.open' },
  { combo: 'F2', key: 'dashboard.keys.rename' },
  { combo: 'Delete', key: 'dashboard.keys.trash' },
  { combo: 'ArrowRight', key: 'dashboard.keys.arrows' },
  { combo: 'mod+a', key: 'dashboard.keys.selectAll' },
  { combo: 'Escape', key: 'dashboard.keys.clear' },
] as const;

export type KeysScope = {
  selection: Selection;
  set: (sel: Selection) => void;
  clear: () => void;
  order: () => string[];
  boards: () => Board[];
  menus: ReadonlyMap<string, ItemMenu<BoardActionId>>;
};

const ARROWS: Record<string, 'left' | 'right' | 'up' | 'down'> = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
const overlayOpen = () => document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"]') !== null;

/** Index of the card the arrow lands on; columns are the cards on the first card's row. */
function step(cards: HTMLElement[], from: number, dir: 'left' | 'right' | 'up' | 'down'): number {
  const top = Math.round(cards[0]!.getBoundingClientRect().top);
  const columns = Math.max(1, cards.filter((c) => Math.round(c.getBoundingClientRect().top) === top).length);
  const to = from + { left: -1, right: 1, up: -columns, down: columns }[dir];
  return to >= 0 && to < cards.length ? to : from;
}

/**
 * Keyboard of a dashboard page: arrows move between cards, Enter opens, F2 renames, Delete trashes (with Undo),
 * mod+A selects all, Escape clears. Nothing happens while text is edited or a dialog or menu is open.
 */
export function useDashboardKeys(root: RefObject<HTMLElement | null>, scope: KeysScope) {
  const { t } = useTranslation();
  const bulk = useBulk();
  const notify = useNotify();
  const latest = useRef({ scope, bulk, notify, t });
  latest.current = { scope, bulk, notify, t };

  useEffect(() => {
    const denied = (reason: Denied) => latest.current.notify(latest.current.t(`dashboard.denied.${reason}`));
    const runOne = (id: string, action: BoardActionId) => {
      const entry = latest.current.scope.menus.get(id)?.entries.find((e) => e.state.id === action);
      if (!entry) return;
      if (!entry.state.allowed) return denied(entry.state.reason);
      latest.current.scope.menus.get(id)!.run(action);
    };

    const onKey = (e: KeyboardEvent) => {
      const { scope: s, bulk: b } = latest.current;
      if (e.defaultPrevented || e.altKey || isEditing(e.target) || overlayOpen()) return;
      const mod = e.metaKey || e.ctrlKey;
      const cards = [...(root.current?.querySelectorAll<HTMLElement>('[data-board-id]') ?? [])];
      const focused = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-board-id]') : null;
      const current = focused && root.current?.contains(focused) ? focused.dataset.boardId! : null;
      const selected = s.boards().filter((x) => s.selection.ids.has(x.id));
      const single = current ?? (selected.length === 1 ? selected[0]!.id : null);
      // Keys belong to the grid: a card, empty space of the page, or nothing focused. A control elsewhere (the bulk bar,
      // the sidebar, a header button) keeps its own keys; Escape still clears the selection from anywhere.
      const target = e.target instanceof Element ? e.target : null;
      const onControl = !current && target?.closest('a, button, input, select, textarea, [role="checkbox"], [role="button"]');
      const inGrid = target === document.body || (!onControl && target !== null && Boolean(root.current?.contains(target))) || Boolean(current);
      if (e.key === 'Escape' && s.selection.ids.size > 0) return s.clear();
      if (!inGrid) return;

      if (mod && e.key.toLowerCase() === 'a') {
        if (cards.length === 0) return;
        e.preventDefault();
        return s.set(selectAll(s.order()));
      }
      if (mod) return;
      if (ARROWS[e.key] && cards.length) {
        e.preventDefault();
        const from = current ? cards.findIndex((c) => c.dataset.boardId === current) : -1;
        const to = from < 0 ? 0 : step(cards, from, ARROWS[e.key]!);
        return cards[to]!.querySelector<HTMLElement>('a')?.focus();
      }
      if (e.key === 'Enter' && !current && single) return navigate(href('board', { boardId: single }));
      if (e.key === 'F2' && single) {
        e.preventDefault();
        return runOne(single, 'rename');
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selected.length > 1 || (selected.length === 1 && !current)) {
          e.preventDefault();
          const allowed = b.allowedFor(selected).find((a) => a.id === 'trash')!.allowedIds;
          if (allowed.length === 0) return denied('notCreator');
          void b.run('trash', selected, allowed).then(s.clear, () => latest.current.notify(latest.current.t('dashboard.bulk.failed')));
          return;
        }
        if (current) {
          e.preventDefault();
          runOne(current, 'trash');
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [root]);
}
